import { Ban, Layers, PauseCircle, Send, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS, type StatusColorTone } from '@/styles/statusColors';
import { formatDisplayDateTime } from '@/utils/date';
import {
  PCMP_CHANNEL_LABELS,
  PCMP_CORRECTION_CATEGORY_CODE,
  PCMP_CORRECTION_COPY,
} from '../../constants/pcmpProposal.constants';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';

interface PcmpProposalBannersProps {
  proposal: Pick<
    PcmpProposalDetail,
    'adminRejection' | 'hold' | 'openRequest' | 'routeBatch' | 'urgentBatch'
  >;
}

interface Banner {
  key: string;
  icon: LucideIcon;
  tone: StatusColorTone;
  title: string;
  body: ReactNode;
}

function buildBanners({
  adminRejection,
  hold,
  openRequest,
  routeBatch,
  urgentBatch,
}: PcmpProposalBannersProps['proposal']): Banner[] {
  const banners: Banner[] = [];
  if (adminRejection) {
    const by = adminRejection.by?.name ? ` by ${adminRejection.by.name}` : '';
    banners.push({
      key: 'admin-rejection',
      icon: Ban,
      tone: 'danger',
      title: `Rejected by Admin from ${adminRejection.stageLabel}`,
      body: (
        <>
          {adminRejection.comment ? (
            <span className="whitespace-pre-line">{adminRejection.comment}</span>
          ) : null}
          <span className="text-xs opacity-80">
            Rejected {formatDisplayDateTime(adminRejection.at)}
            {by}
          </span>
        </>
      ),
    });
  }
  if (hold) {
    banners.push({
      key: 'hold',
      icon: PauseCircle,
      tone: 'warning',
      title: `On hold at ${hold.stageLabel}`,
      body: (
        <>
          {hold.reason ? <span className="whitespace-pre-line">{hold.reason}</span> : null}
          <span className="text-xs opacity-80">Held {formatDisplayDateTime(hold.heldAt)}</span>
        </>
      ),
    });
  }
  if (openRequest) {
    banners.push({
      key: 'request',
      icon: Send,
      tone: 'info',
      title: `Open request: ${openRequest.categoryName || 'Request'} (${openRequest.status.toLowerCase()})`,
      body: (
        <>
          {openRequest.note ? (
            <span className="whitespace-pre-line">{openRequest.note}</span>
          ) : null}
          {openRequest.categoryCode === PCMP_CORRECTION_CATEGORY_CODE ? (
            <span>{PCMP_CORRECTION_COPY.banner}</span>
          ) : null}
          <span className="text-xs opacity-80">
            Raised {formatDisplayDateTime(openRequest.raisedAt)}
          </span>
        </>
      ),
    });
  }
  if (routeBatch) {
    const channel = routeBatch.channel ? ` · ${PCMP_CHANNEL_LABELS[routeBatch.channel]}` : '';
    banners.push({
      key: 'route',
      icon: Layers,
      tone: 'info',
      title: `Batch #${routeBatch.batchNo}${channel}`,
      body: <span className="text-xs">Status: {routeBatch.status.toLowerCase()}</span>,
    });
  }
  if (urgentBatch) {
    banners.push({
      key: 'urgent',
      icon: Layers,
      tone: 'info',
      title: `Urgent batch #${urgentBatch.batchNo}`,
      body: <span className="text-xs">Status: {urgentBatch.status.toLowerCase()}</span>,
    });
  }
  return banners;
}

export function PcmpProposalBanners({ proposal }: PcmpProposalBannersProps) {
  const banners = buildBanners(proposal);
  if (banners.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {banners.map(({ key, icon: Icon, tone, title, body }) => (
        <div
          key={key}
          className={cn('flex items-start gap-3 rounded-lg px-4 py-3', STATUS_COLOR_CLASS[tone])}
        >
          <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="flex min-w-0 flex-col gap-0.5 text-sm">
            <span className="font-medium">{title}</span>
            {body}
          </div>
        </div>
      ))}
    </div>
  );
}
